import { range } from 'lodash-es';
import {
  SUFFIX,
  convertCelsiusToFahrenheit,
  convertFahrenheitToCelsius,
} from '../../../shared/common/units';

export interface TemperatureRangeDefinition {
  min: number;
  max: number;
  stepping: number;
}

export interface SelectTemperatureValue {
  viewValue: string;
  value: number;
}

const rangeInclusiveMargin = 0.01;

const toPrecision2 = (number: number): number => Math.round(number * 100) / 100;

const roundToStepping = (temperature: number, stepping: number): number => {
  if (stepping <= 0) {
    return temperature;
  }

  const lowerStep = toPrecision2(Math.floor(temperature / stepping) * stepping);
  const upperStep = toPrecision2(Math.ceil(temperature / stepping) * stepping);

  const distanceToCeil = toPrecision2(upperStep - temperature);
  const halfStep = toPrecision2(stepping / 2);

  return distanceToCeil <= halfStep ? upperStep : lowerStep;
};

export function getCelsiusFromFahrenheit(fahrenheit: number, precision: number): number {
  const celsius = convertFahrenheitToCelsius(fahrenheit);
  if (precision > 0) {
    return Number(celsius.toFixed(precision));
  }

  return Number(celsius.toFixed());
}

export function getFahrenheitCeilValueFromCelsius(celsius: number, precision: number): number {
  const fahrenheit = convertCelsiusToFahrenheit(celsius);
  if (precision > 0) {
    return Number(fahrenheit.toFixed(precision));
  }

  return Math.ceil(fahrenheit);
}

export function getFahrenheitFloorValueFromCelsius(celsius: number, precision: number): number {
  const fahrenheit = convertCelsiusToFahrenheit(celsius);
  if (precision > 0) {
    return Number(fahrenheit.toFixed(precision));
  }

  return Math.floor(fahrenheit);
}

export const convertToFahrenheitRounded = (celcius: number, stepping = 1): number => {
  return roundToStepping(celcius * 1.8 + 32, stepping);
};

export const convertToGallons = (liters: number, stepping = 1): number => {
  return roundToStepping(liters * 0.26417287472922, stepping);
};

export const convertToGallonsPerMinute = (
  litersPerMinutes: number,
  minutes: number,
  stepping = 1
): number => {
  return roundToStepping((litersPerMinutes * 0.26417287472922) / minutes, stepping);
};

export const convertToLitersPerMinute = (
  gallonsPerMinutes: number,
  minutes: number,
  stepping = 1
): number => {
  return roundToStepping((gallonsPerMinutes / 0.26417287472922) * minutes, stepping);
};

export const convertToCubicFeet = (cubicMeter: number, stepping = 1): number => {
  return roundToStepping(cubicMeter * 35.3147, stepping);
};

export const convertToCubicMeter = (cubicFeet: number, stepping = 1): number => {
  return roundToStepping(cubicFeet / 35.3147, stepping);
};

export const convertToPoundPerSquareInch = (bar: number, stepping = 1): number => {
  return roundToStepping(bar * 14.504, stepping);
};

export const convertToBar = (poundPerSquareInch: number, stepping = 1): number => {
  return roundToStepping(poundPerSquareInch / 14.504, stepping);
};

export const generateTemperatureValues = (
  definition: TemperatureRangeDefinition,
  isCelsius = true,
  outOfRangeValue: number = null
): SelectTemperatureValue[] => {
  if (!definition) {
    return [];
  }

  const rangeValues: number[] = range(
    definition.min,
    definition.max + rangeInclusiveMargin,
    definition.stepping
  );
  if (outOfRangeValue !== null) {
    const insertPosition: number = outOfRangeValue < definition.min ? 0 : rangeValues.length;
    rangeValues.splice(insertPosition, 0, outOfRangeValue);
  }

  const celsiusValues = rangeValues.map(toPrecision2);

  switch (isCelsius) {
    case false:
      return celsiusValues
        .map((value) => ({
          celsius: value,
          farenheit: convertToFahrenheitRounded(value, definition.stepping),
        }))
        .map((value) => ({
          value: value.celsius,
          viewValue: `${value.farenheit} ${SUFFIX.fahrenheit}`,
        }));

    case true:
    default:
      return celsiusValues.map((value) => ({ value, viewValue: `${value} ${SUFFIX.celsius}` }));
  }
};

export const getOutOfRangeValue = (
  rangeDefinition: TemperatureRangeDefinition,
  outOfRangeValueCandidate: number
): number | null => {
  const { min, max, stepping } = rangeDefinition;

  const adjustedMax = max + rangeInclusiveMargin;

  const inRange = outOfRangeValueCandidate >= min && outOfRangeValueCandidate <= adjustedMax;

  const isAligned = (outOfRangeValueCandidate - min) % stepping === 0;

  if (!inRange || !isAligned) {
    return outOfRangeValueCandidate;
  }

  return null;
};
